import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getEditItem,
  getIsItemModalOpen,
  toggleItemModal,
} from "../../features/items/itemsSlice";

import styles from "./ItemFormModal.module.scss";
import { IFormInput, OptionsProps } from "../../types/item";
import Select from "react-select";
import { selectStyles } from "../../static/styles";

type Props = {
  handleSaveItem: (data: IFormInput) => void;
};

const ItemFormModal = ({ handleSaveItem }: Props) => {
  const dispatch = useAppDispatch();
  const editItem = useAppSelector(getEditItem);
  const isItemModalOpen = useAppSelector(getIsItemModalOpen);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [inInchOption] = useState<OptionsProps[]>([
    {
      value: 0,
      label: "ઇંચ માં નથી",
    },
    {
      value: 1,
      label: "ઇંચ માં છે",
    },
  ]);

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm<IFormInput>({
    defaultValues: { name: "", description: "", in_inch: 0 },
  });

  const handleOnSubmit: SubmitHandler<IFormInput> = async (data: any) => {
    setIsLoading(true);
    await handleSaveItem(data);
    handleCloseFormModal();
    setIsLoading(false);
  };

  const handleCloseFormModal = () => {
    reset();
    dispatch(toggleItemModal(false));
  };

  useEffect(() => {
    if (editItem && editItem.id) {
      reset({
        name: editItem.name,
        description: editItem.description,
        in_inch: editItem.in_inch || 0,
      });
    } else {
      reset({ name: "", description: "", in_inch: 0 });
    }
  }, [editItem, isItemModalOpen]);

  return (
    <>
      {isItemModalOpen === true && (
        <div className={styles.ItemFormModal}>
          <div className="relative h-[100vh] w-[100vw] overflow-y-scroll lg:overflow-auto top-0 lg:top-10 lg:mx-auto p-5 border lg:w-96 lg:h-fit shadow-lg lg:rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-2xl text-center font-bold leading-6 text-primary-1">
                આઈટમ ની માહિતી
              </h3>
              <div className="mt-2 py-3">
                <form onSubmit={handleSubmit(handleOnSubmit)}>
                  <div className="grid gap-4">
                    <div className="mt-6">
                      <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-14 w-full relative rounded">
                        <input
                          className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          type="text"
                          placeholder="આઈટમનુ નામ દાખલ કરો"
                          {...register("name", { required: true })}
                        />
                      </div>
                      {errors.name && errors.name.type === "required" && (
                        <span className="text-red-500">
                          આઈટમનુ નામ દાખલ કરો
                        </span>
                      )}
                    </div>

                    <div className="mt-3">
                      <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-28 w-full relative rounded">
                        <textarea
                          rows={5}
                          className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          placeholder="આઈટમનુ ડિસ્ક્રિપ્શન દાખલ કરો"
                          {...register("description", { required: false })}
                        />
                      </div>
                      {errors.description &&
                        errors.description.type === "required" && (
                          <span className="text-red-500">
                            આઈટમનુ ડિસ્ક્રિપ્શન દાખલ કરો
                          </span>
                        )}
                    </div>

                    <div className="mt-3">
                      <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-12 w-full relative rounded">
                        <Controller
                          {...register("in_inch")}
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              value={
                                inInchOption.find(
                                  (val) => val.value === field.value
                                ) || null
                              }
                              className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                              styles={selectStyles}
                              placeholder={"ઇંચ નું સ્ટેટ્સ સિલેક્ટ કરો"}
                              options={inInchOption}
                              onChange={(val: any) =>
                                setValue("in_inch", val?.value)
                              }
                            />
                          )}
                        />
                      </div>

                      {errors?.in_inch && (
                        <span className="text-red-500">
                          ઇંચ નું સ્ટેટ્સ સિલેક્ટ કરો
                        </span>
                      )}
                    </div>

                    <div className="mt-3 text-center">
                      <button
                        className="mx-auto shadow disabled:opacity-50 rounded-3xl text-3xl lg:text-xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                        type="submit"
                        disabled={isLoading}
                      >
                        {isLoading ? "રાહ જુઓ..." : "આગળ વધો"}
                      </button>
                    </div>

                    <div className="mt-1 text-center">
                      <button
                        className="mx-auto shadow disabled:opacity-50 rounded-3xl text-3xl lg:text-xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                        type="button"
                        onClick={handleCloseFormModal}
                        disabled={isLoading}
                      >
                        બંધ કરો
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ItemFormModal;
